/* eslint-disable react/display-name */
import React, { useState, useMemo } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, useMutation, gql } from '@apollo/client'
import { getUsers, deleteUser, restoreUser } from '../apollo'
import {
  Box, Container, Typography, Paper, InputBase,
  Avatar, Chip, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, Divider, Button
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PeopleIcon from '@mui/icons-material/People'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import DeleteIcon from '@mui/icons-material/Delete'
import RestoreIcon from '@mui/icons-material/Restore'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CloseIcon from '@mui/icons-material/Close'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'

const GET_USERS = gql`${getUsers}`
const DELETE_USER = gql`${deleteUser}`
const RESTORE_USER = gql`${restoreUser}`

const stringToColor = s => {
  if (!s) return '#94a3b8'
  let h = 0
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h)
  const palette = ['#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#22c55e', '#14b8a6', '#f59e0b', '#6366f1']
  return palette[Math.abs(h) % palette.length]
}

const getInitials = name => {
  if (!name) return '?'
  const parts = name.trim().split(' ')
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name[0].toUpperCase()
}

const formatDate = iso => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d)) return '—'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function Users() {
  const [searchQuery, setSearchQuery] = useState('')
  const [viewUser, setViewUser] = useState(null)
  const { data, error, loading } = useQuery(GET_USERS, { variables: { page: 0 } })
  const [confirmUser, setConfirmUser] = useState(null)
  const [actionError, setActionError] = useState('')
  const [mutateDelete, { loading: deleting }] = useMutation(DELETE_USER, {
    refetchQueries: [{ query: GET_USERS, variables: { page: 0 } }],
    onCompleted: () => setConfirmUser(null),
    onError: err => setActionError(err.message)
  })
  const [mutateRestore] = useMutation(RESTORE_USER, {
    refetchQueries: [{ query: GET_USERS, variables: { page: 0 } }],
    onError: err => setActionError(err.message)
  })

  // Erased accounts stay listed so their orders still have a customer, but they
  // aren't customers any more.
  const allUsers = data?.users ?? []
  const customers = allUsers.filter(u => !u.erasedAt)
  const withEmail = customers.filter(u => !!u.email).length
  const withPhone = customers.filter(u => !!u.phone).length

  const filtered = useMemo(() => {
    if (searchQuery.length < 2) return allUsers
    const q = searchQuery.toLowerCase()
    return allUsers.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.phone || '').toLowerCase().includes(q)
    )
  }, [allUsers, searchQuery])

  return (
    <>
      {/* Hero banner */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PeopleIcon sx={{ color: '#93c5fd', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15 }}>Customer Management</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>View and manage all registered customers</Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        {/* Stat cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
          {[
            { label: 'Total Customers', value: customers.length, icon: <PeopleIcon sx={{ color: '#3b82f6', fontSize: 26 }} />, bg: '#eff6ff', border: '#bfdbfe' },
            { label: 'With Email', value: withEmail, icon: <EmailIcon sx={{ color: '#8b5cf6', fontSize: 26 }} />, bg: '#f5f3ff', border: '#ddd6fe' },
            { label: 'With Phone', value: withPhone, icon: <PhoneIcon sx={{ color: '#22c55e', fontSize: 26 }} />, bg: '#f0fdf4', border: '#bbf7d0' },
          ].map((card, i) => (
            <Paper key={i} elevation={0} sx={{ borderRadius: '16px', border: `1px solid ${card.border}`, p: 2.5, display: 'flex', alignItems: 'center', gap: 2, background: card.bg, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
              <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                {card.icon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                  {loading ? <CircularProgress size={20} /> : card.value}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#64748b', mt: 0.3 }}>{card.label}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Table */}
        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
              All Customers{' '}
              <Typography component="span" sx={{ fontSize: '0.85rem', color: '#94a3b8', fontWeight: 400 }}>({filtered.length})</Typography>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', px: 1.5, py: 0.8, width: 280 }}>
              <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
              <InputBase placeholder="Search customers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} sx={{ fontSize: '0.875rem', flex: 1, color: '#475569' }} />
            </Box>
          </Box>

          {error && <Box sx={{ px: 3, py: 2, bgcolor: '#fef2f2' }}><Typography sx={{ fontSize: '0.83rem', color: '#dc2626' }}>Error: {error.message}</Typography></Box>}

          {actionError && <Box sx={{ px: 3, py: 2, bgcolor: '#fef2f2' }}><Typography sx={{ fontSize: '0.83rem', color: '#dc2626' }}>{actionError}</Typography></Box>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              {/* Table header */}
              <Box sx={{ minWidth: 760, display: 'grid', gridTemplateColumns: '2fr 2fr 1.5fr 1fr 1fr 100px', px: 3, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {['Name', 'Email', 'Phone', 'Roles', 'Created', 'Actions'].map((h, i) => (
                  <Typography key={i} sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</Typography>
                ))}
              </Box>

              {filtered.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <PeopleIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                  <Typography sx={{ color: '#94a3b8' }}>No customers found</Typography>
                </Box>
              ) : filtered.map((u, i) => (
                <Box key={u._id} sx={{ minWidth: 760, display: 'grid', gridTemplateColumns: '2fr 2fr 1.5fr 1fr 1fr 100px', px: 3, py: 1.8, alignItems: 'center', borderBottom: i < filtered.length - 1 ? '1px solid #f1f5f9' : 'none', '&:hover': { bgcolor: '#fafafa' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: stringToColor(u.name || u.email), fontSize: '0.82rem', fontWeight: 700, flexShrink: 0 }}>
                      {getInitials(u.name || u.email)}
                    </Avatar>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>{u.name || '—'}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>{u.erasedAt ? '—' : u.email || '—'}</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>{u.phone || '—'}</Typography>
                  {u.erasedAt ? (
                    <Chip label="Erased" size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 600, fontSize: '0.72rem', height: 22, width: 'fit-content' }} />
                  ) : u.isActive === false ? (
                    <Box>
                      <Chip label="Deleted" size="small" sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 600, fontSize: '0.72rem', height: 22, width: 'fit-content' }} />
                      <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.3 }}>Erases {new Date(u.erasesAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</Typography>
                    </Box>
                  ) : (
                    <Chip label="Customer" size="small" sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600, fontSize: '0.72rem', height: 22, width: 'fit-content' }} />
                  )}
                  <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>{formatDate(u.createdAt)}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => setViewUser(u)} sx={{ color: '#3b82f6', bgcolor: '#eff6ff', borderRadius: '8px', width: 30, height: 30, '&:hover': { bgcolor: '#dbeafe' } }}>
                      <VisibilityIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                    {u.isActive === false ? (
                      !u.erasedAt && (
                        <IconButton size="small" aria-label="Restore account" title="Restore account" onClick={() => { setActionError(''); mutateRestore({ variables: { id: u._id } }) }} sx={{ color: '#16a34a', bgcolor: '#f0fdf4', borderRadius: '8px', '&:hover': { bgcolor: '#dcfce7' } }}>
                          <RestoreIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      )
                    ) : (
                      <IconButton size="small" aria-label="Delete account" title="Delete account" onClick={() => { setActionError(''); setConfirmUser(u) }} sx={{ color: '#ef4444', bgcolor: '#fef2f2', borderRadius: '8px', width: 30, height: 30, '&:hover': { bgcolor: '#fee2e2' } }}>
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Container>

      {/* View Customer Modal */}
      {/* Delete: the same 30 days a customer gets. Orders are never removed. */}
      <Dialog open={!!confirmUser} onClose={() => setConfirmUser(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#1e293b' }}>
          Delete {confirmUser?.name || confirmUser?.email || 'this account'}?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem', color: '#475569', lineHeight: 1.55 }}>
            They are signed out on every device and any Black Card billing stops. They have 30 days to restore the account, then their personal details are erased. Their orders are always kept.
          </Typography>
          {actionError && <Typography sx={{ fontSize: '0.83rem', color: '#dc2626', mt: 1.5 }}>{actionError}</Typography>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
            <Button onClick={() => setConfirmUser(null)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
            <Button
              variant="contained"
              disableElevation
              disabled={deleting}
              onClick={() => { setActionError(''); mutateDelete({ variables: { id: confirmUser._id } }) }}
              sx={{ textTransform: 'none', bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
              {deleting ? 'Deleting…' : 'Delete account'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewUser} onClose={() => setViewUser(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}>
        {viewUser && (
          <>
            {/* Modal header with gradient */}
            <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)', px: 3, pt: 3, pb: 4, position: 'relative' }}>
              <IconButton size="small" onClick={() => setViewUser(null)} sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,0.7)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: stringToColor(viewUser.name || viewUser.email), fontSize: '1.2rem', fontWeight: 700, border: '3px solid rgba(255,255,255,0.3)' }}>
                  {getInitials(viewUser.name || viewUser.email)}
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#fff' }}>{viewUser.name || 'Unknown'}</Typography>
                  <Chip label="Customer" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: '0.72rem', height: 20, mt: 0.5 }} />
                </Box>
              </Box>
            </Box>

            <DialogContent sx={{ px: 3, pt: 2.5, pb: 3 }}>
              {/* Info rows */}
              {[
                { icon: <EmailIcon sx={{ fontSize: 16, color: '#8b5cf6' }} />, label: 'Email', value: viewUser.email || '—' },
                { icon: <PhoneIcon sx={{ fontSize: 16, color: '#22c55e' }} />, label: 'Phone', value: viewUser.phone || '—' },
                { icon: <CalendarTodayIcon sx={{ fontSize: 16, color: '#3b82f6' }} />, label: 'Joined', value: formatDate(viewUser.createdAt) },
              ].map((row, i) => (
                <Box key={i}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {row.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{row.label}</Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: '#1e293b', fontWeight: 500 }}>{row.value}</Typography>
                    </Box>
                  </Box>
                  {i < 2 && <Divider />}
                </Box>
              ))}

              {/* Addresses */}
              {(viewUser.addresses ?? []).length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>Saved Addresses</Typography>
                  {viewUser.addresses.map((a, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, p: 1.5, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                      <LocationOnIcon sx={{ fontSize: 15, color: '#94a3b8', mt: '2px', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.82rem', color: '#475569' }}>{a.deliveryAddress || '—'}</Typography>
                    </Box>
                  ))}
                </>
              )}

              <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={() => setViewUser(null)} sx={{ borderRadius: '10px', textTransform: 'none', color: '#64748b', fontWeight: 600 }}>Close</Button>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  )
}

export default withTranslation()(Users)